import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './client';

/**
 * Data-fetching hooks that wrap the typed `api` methods with standard
 * loading / error / data state, so every screen handles the three states
 * the same way.
 *
 *   const { data, loading, error, reload } = useApi(() => api.orders.list({ status }), [status]);
 *   if (loading) return <LoadingState />;
 *   if (error)   return <ErrorState message={error.message} onRetry={reload} />;
 *   return <OrdersTable rows={data.items} />;
 */

export interface UseApiResult<T> {
  data: T | undefined;
  loading: boolean;
  error: ApiError | undefined;
  /** Re-run the fetcher (e.g. retry button / after a mutation). */
  reload: () => void;
}

function toApiError(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  if (e instanceof Error) return new ApiError(0, e.message, e);
  return new ApiError(0, 'Unexpected error', e);
}

/**
 * Fetch on mount and whenever `deps` change. The fetcher receives an
 * `AbortSignal` so in-flight requests are cancelled on unmount / dep change.
 */
export function useApi<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: React.DependencyList = [],
): UseApiResult<T> {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError>();
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError(undefined);
    fetcherRef
      .current(controller.signal)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((e: unknown) => {
        if (active && controller.signal.aborted) return;
        if (active) setError(toApiError(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, reload };
}

export interface UseMutationResult<TArgs extends unknown[], TResult> {
  /** Invoke the mutation; resolves with the result or rejects with an ApiError. */
  mutate: (...args: TArgs) => Promise<TResult>;
  loading: boolean;
  error: ApiError | undefined;
  data: TResult | undefined;
  reset: () => void;
}

/**
 * Imperative mutation wrapper for create/update/confirm actions.
 *
 *   const create = useMutation(api.orders.create);
 *   await create.mutate(dto);  // create.loading / create.error track state
 */
export function useMutation<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
): UseMutationResult<TArgs, TResult> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError>();
  const [data, setData] = useState<TResult>();
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const mutate = useCallback(async (...args: TArgs) => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await fnRef.current(...args);
      setData(result);
      return result;
    } catch (e) {
      const apiError = toApiError(e);
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(undefined);
    setData(undefined);
    setLoading(false);
  }, []);

  return { mutate, loading, error, data, reset };
}
